import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { stashPendingLegalAcceptance } from '@/lib/legalAcceptance';
import { LEGAL_VERSION } from '@/lib/marketing/legal/version';
import { Star, Building2, Users, Loader2, ArrowLeft } from 'lucide-react';
import AuthMarketingPanel, { HiroMark } from '@/components/auth/AuthMarketingPanel';
import { api } from '@/services/api';
import { cn } from '@/lib/utils';
import { AGENCY_SELF_SERVE_ENABLED } from '@/lib/productFlags';
import { AgencyContactModal } from '@/components/billing/AgencyContactModal';
import { PageSeo } from '@/components/seo/PageSeo';
import { NOINDEX_FOLLOW } from '@/lib/seo/constants';
import { signupSeo } from '@/lib/seo/authSeo';

type Step = 'credentials' | 'accountType' | 'agencyTier';
type AgencyPlan = 'agency_starter' | 'agency_professional';
type AgencyPeriod = 'monthly' | 'annual';

const VALID_STEPS: ReadonlyArray<Step> = ['credentials', 'accountType', 'agencyTier'];

const SignupPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [agencyPlan, setAgencyPlan] = useState<AgencyPlan>('agency_professional');
  const [agencyPeriod, setAgencyPeriod] = useState<AgencyPeriod>('annual');
  const [agencySubmitting, setAgencySubmitting] = useState(false);
  const [agencyConflict, setAgencyConflict] = useState(false);
  const [agencyContactOpen, setAgencyContactOpen] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedOnerousClauses, setAcceptedOnerousClauses] = useState(false);
  const { signup, loginWithGoogle, isLoading, refreshSession, user } = useAuth();
  const { t, language } = useLanguage();
  const isIt = language === 'it';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') ?? '/dashboard';

  // Persist the wizard step in the URL so that any re-mount of the page
  // (route prop changes, AuthContext loading flips, hot reload, browser
  // back/forward) restores the user where they were instead of resetting
  // to the credentials form.
  const stepFromUrl = searchParams.get('step');
  const initialStep: Step = VALID_STEPS.includes(stepFromUrl as Step)
    ? (stepFromUrl as Step)
    : 'credentials';
  const [step, setStepState] = useState<Step>(initialStep);

  const setStep = (next: Step) => {
    setStepState(next);
    const params = new URLSearchParams(searchParams);
    if (next === 'credentials') {
      params.delete('step');
    } else {
      params.set('step', next);
    }
    setSearchParams(params, { replace: true });
  };

  // Auto-redirect ONLY when the user is already fully onboarded (has at
  // least one company membership). This avoids hijacking the signup flow
  // between the credentials step and the account-type chooser.
  // App.tsx intentionally leaves this Route un-guarded for that reason.
  useEffect(() => {
    if (user && Object.keys(user.memberships ?? {}).length > 0) {
      navigate(redirectTo, { replace: true });
    }
  }, [user, redirectTo, navigate]);

  // Agency self-serve pause: block deep-link to agencyTier wizard step.
  useEffect(() => {
    if (!AGENCY_SELF_SERVE_ENABLED && step === 'agencyTier') {
      setStep('accountType');
    }
  }, [step]);

  const onboardingUrl =
    redirectTo !== '/dashboard'
      ? `/onboarding?fromSignup=true&skipCompany=true&redirect=${encodeURIComponent(redirectTo)}`
      : '/onboarding?fromSignup=true';

  const legalGateOk = acceptedTerms && acceptedOnerousClauses;

  const ensureLegalGate = (): boolean => {
    if (legalGateOk) return true;
    setError(
      isIt
        ? 'Devi accettare i Termini di Servizio e approvare le clausole indicate per continuare.'
        : 'You must accept the Terms of Service and approve the listed clauses to continue.',
    );
    return false;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ensureLegalGate()) return;
    if (password !== confirm) {
      setError(t('signup.passwordMismatch'));
      return;
    }
    setError('');
    try {
      stashPendingLegalAcceptance();
      await signup(name, email, password);
      setStep('accountType');
    } catch (err: any) {
      setError(err?.message || t('signup.error'));
    }
  };

  const handleSelectSme = () => {
    navigate(onboardingUrl, { replace: true });
  };

  const handleConfirmAgency = async () => {
    setAgencySubmitting(true);
    setError('');
    setAgencyConflict(false);
    try {
      await api.auth.updateProfile({
        accountType: 'agency_owner',
        agencyPlanId: agencyPlan,
        agencyBillingPeriod: agencyPeriod,
      });
      await refreshSession().catch(() => {});
      navigate(onboardingUrl, { replace: true });
    } catch (err: any) {
      const msg = err?.message || t('signup.agencyTier.error');
      if (err?.status === 409 || /already/i.test(msg)) {
        setAgencyConflict(true);
      }
      setError(msg);
    } finally {
      setAgencySubmitting(false);
    }
  };

  const seo = signupSeo(language);

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-brand-surface-light">
      <PageSeo title={seo.title} path="/signup" robots={NOINDEX_FOLLOW} />
      <AuthMarketingPanel />

      <div className="flex flex-col items-center justify-center px-5 sm:px-8 py-10 lg:py-12">
        <div className={cn('w-full', step === 'accountType' ? 'max-w-2xl' : 'max-w-sm')}>
          <div className="flex lg:hidden items-center justify-center gap-2 mb-8">
            <HiroMark className="h-9 w-9" />
            <span className="font-display font-bold text-xl tracking-tight">HiroMatch</span>
          </div>

          {step === 'credentials' && (
          <>
          <div className="mb-6">
            <div className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-accent bg-brand-accent/10 px-2.5 py-1 rounded-full mb-4">
              <Star className="h-3 w-3 fill-current" />
              4.8 · Demo team available
            </div>
            <h1 className="font-display font-bold text-3xl tracking-tight text-foreground leading-tight">
              {isIt ? 'Inizia il tuo trial gratuito di 7 giorni' : 'Start your free 7-day trial'}
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              {isIt
                ? 'Nessuna carta richiesta. Cancellabile in qualsiasi momento.'
                : 'No credit card required. Cancel anytime.'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="space-y-2">
              <Label htmlFor="name">{t('signup.name')}</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required placeholder={t('signup.namePlaceholder')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">{t('signup.email')}</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@company.com" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('signup.password')}</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm">{t('signup.confirm')}</Label>
              <Input id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required placeholder="••••••••" />
            </div>

            <div className="space-y-3 rounded-md border border-border/60 bg-card/50 p-4 text-sm">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="accept-terms"
                  checked={acceptedTerms}
                  onCheckedChange={(v) => setAcceptedTerms(v === true)}
                  className="mt-0.5"
                />
                <label htmlFor="accept-terms" className="leading-relaxed text-muted-foreground cursor-pointer">
                  {isIt ? (
                    <>
                      Dichiaro di agire nell&apos;ambito della mia attività professionale o imprenditoriale e accetto i{' '}
                      <Link to="/terms" target="_blank" className="text-brand-accent underline-offset-4 hover:underline">
                        Termini di Servizio
                      </Link>{' '}
                      (versione {LEGAL_VERSION}) e l&apos;{' '}
                      <Link to="/privacy" target="_blank" className="text-brand-accent underline-offset-4 hover:underline">
                        Informativa Privacy
                      </Link>
                      .
                    </>
                  ) : (
                    <>
                      I declare that I act in the course of my professional or business activity and accept the{' '}
                      <Link to="/terms" target="_blank" className="text-brand-accent underline-offset-4 hover:underline">
                        Terms of Service
                      </Link>{' '}
                      (version {LEGAL_VERSION}) and the{' '}
                      <Link to="/privacy" target="_blank" className="text-brand-accent underline-offset-4 hover:underline">
                        Privacy Policy
                      </Link>
                      .
                    </>
                  )}
                </label>
              </div>
              <div className="flex items-start gap-3">
                <Checkbox
                  id="accept-onerous"
                  checked={acceptedOnerousClauses}
                  onCheckedChange={(v) => setAcceptedOnerousClauses(v === true)}
                  className="mt-0.5"
                />
                <label htmlFor="accept-onerous" className="leading-relaxed text-muted-foreground cursor-pointer">
                  {isIt
                    ? 'Ai sensi degli artt. 1341 e 1342 c.c., approvo specificamente le seguenti clausole dei Termini di Servizio: art. 2 (modifica del Servizio), art. 3 (sospensione e chiusura account), art. 4 (rinnovo automatico, sospensione per mancato pagamento, esclusione rimborsi), art. 9 (limitazione di responsabilità), art. 10 (manleva), art. 11 (risoluzione e sospensione), art. 12 (forza maggiore e recesso), art. 13 (foro esclusivo di Roma e legge applicabile).'
                    : 'Pursuant to Articles 1341 and 1342 of the Italian Civil Code, I specifically approve the following clauses of the Terms of Service: Art. 2 (changes to the Service), Art. 3 (account suspension and closure), Art. 4 (automatic renewal, suspension for non-payment, refund exclusions), Art. 9 (limitation of liability), Art. 10 (indemnification), Art. 11 (termination and suspension), Art. 12 (force majeure and withdrawal), Art. 13 (exclusive jurisdiction of Rome and governing law).'}
                </label>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-brand-primary hover:bg-brand-primary-mid text-white"
              disabled={isLoading || !legalGateOk}
            >
              {isLoading ? t('signup.submitting') : t('signup.submit')}
            </Button>

            <div className="relative py-1">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
              <div className="relative flex justify-center"><span className="bg-brand-surface-light px-2 text-xs text-muted-foreground uppercase tracking-wide">{isIt ? 'oppure' : 'or'}</span></div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full gap-2"
              disabled={isLoading}
              onClick={async () => {
                if (!ensureLegalGate()) return;
                try {
                  stashPendingLegalAcceptance();
                  await loginWithGoogle();
                  // Existing Google user: useEffect on `user.memberships`
                  // redirects to /dashboard if they're already onboarded.
                  // Otherwise advance to the SME/Agency chooser like for
                  // a normal email signup.
                  setStep('accountType');
                } catch (err: any) {
                  // AuthContext.loginWithGoogle throws NEW_GOOGLE_USER for
                  // brand-new Google accounts AFTER the auth succeeded.
                  // Treat it as success and advance to the chooser instead
                  // of showing a generic "Google signup failed" error.
                  if (err?.code === 'NEW_GOOGLE_USER') {
                    setStep('accountType');
                    return;
                  }
                  setError(t('signup.googleFailed'));
                }
              }}
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              {t('signup.google')}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-8">
            {isIt ? 'Hai già un account? ' : 'Already have an account? '}
            <Link
              to={redirectTo !== '/dashboard' ? `/login?redirect=${encodeURIComponent(redirectTo)}` : '/login'}
              className="text-brand-accent hover:text-brand-primary font-medium"
            >
              {isIt ? 'Accedi →' : 'Sign in →'}
            </Link>
          </p>
          </>
          )}

          {step === 'accountType' && (
            <div>
              <h1 className="font-display font-bold text-2xl sm:text-3xl tracking-tight text-foreground leading-tight text-center mb-8">
                {t('signup.accountType.title')}
              </h1>
              <div className={cn(
                'grid gap-4',
                AGENCY_SELF_SERVE_ENABLED ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 max-w-md mx-auto',
              )}>
                <button
                  type="button"
                  onClick={handleSelectSme}
                  className="text-left rounded-lg border border-border bg-card hover:border-brand-primary hover:shadow-md transition-all p-6 flex flex-col gap-3"
                >
                  <Building2 className="h-8 w-8 text-brand-primary" />
                  <div className="font-display font-semibold text-lg">{t('signup.accountType.sme.title')}</div>
                  <p className="text-sm text-muted-foreground flex-1">{t('signup.accountType.sme.desc')}</p>
                  <div className="text-sm font-medium text-foreground">{t('signup.accountType.sme.price')}</div>
                  <div className="inline-flex items-center justify-center text-sm font-medium text-brand-primary mt-2">
                    {t('signup.accountType.sme.cta')} →
                  </div>
                </button>
                {AGENCY_SELF_SERVE_ENABLED && (
                  <button
                    type="button"
                    onClick={() => setStep('agencyTier')}
                    className="text-left rounded-lg border border-border bg-card hover:border-brand-primary hover:shadow-md transition-all p-6 flex flex-col gap-3"
                  >
                    <Users className="h-8 w-8 text-brand-accent" />
                    <div className="font-display font-semibold text-lg">{t('signup.accountType.agency.title')}</div>
                    <p className="text-sm text-muted-foreground flex-1">{t('signup.accountType.agency.desc')}</p>
                    <div className="text-sm font-medium text-foreground">{t('signup.accountType.agency.price')}</div>
                    <div className="inline-flex items-center justify-center text-sm font-medium text-brand-accent mt-2">
                      {t('signup.accountType.agency.cta')} →
                    </div>
                  </button>
                )}
              </div>
              {!AGENCY_SELF_SERVE_ENABLED && (
                <p className="text-center text-sm text-muted-foreground mt-6">
                  {isIt ? 'Sei un\'agenzia di recruiting? ' : 'Running a recruiting agency? '}
                  <button
                    type="button"
                    className="text-brand-accent hover:text-brand-primary font-medium underline-offset-2 hover:underline"
                    onClick={() => setAgencyContactOpen(true)}
                  >
                    {t('agency.contactModal.cta')}
                  </button>
                </p>
              )}
            </div>
          )}

          {AGENCY_SELF_SERVE_ENABLED && step === 'agencyTier' && (
            <div>
              <button
                type="button"
                onClick={() => { setStep('accountType'); setError(''); setAgencyConflict(false); }}
                className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
              >
                <ArrowLeft className="h-4 w-4" /> {t('signup.agencyTier.back')}
              </button>
              <h1 className="font-display font-bold text-2xl tracking-tight text-foreground leading-tight mb-6">
                {t('signup.agencyTier.title')}
              </h1>
              <div className="space-y-3">
                {([
                  { id: 'agency_starter' as const, title: t('signup.agencyTier.starter'), desc: t('signup.agencyTier.starterDesc') },
                  { id: 'agency_professional' as const, title: t('signup.agencyTier.pro'), desc: t('signup.agencyTier.proDesc') },
                ]).map(opt => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setAgencyPlan(opt.id)}
                    className={cn(
                      'w-full text-left rounded-lg border p-4 transition-all flex items-start gap-3',
                      agencyPlan === opt.id
                        ? 'border-brand-primary bg-brand-primary/5 ring-1 ring-brand-primary'
                        : 'border-border hover:border-brand-primary/50',
                    )}
                  >
                    <span
                      className={cn(
                        'mt-1 h-4 w-4 rounded-full border-2 flex-shrink-0',
                        agencyPlan === opt.id ? 'border-brand-primary bg-brand-primary' : 'border-border',
                      )}
                    />
                    <div>
                      <div className="font-medium text-foreground">{opt.title}</div>
                      <div className="text-sm text-muted-foreground">{opt.desc}</div>
                    </div>
                  </button>
                ))}
              </div>

              <div className="mt-6">
                <div className="text-sm font-medium text-foreground mb-2">{t('signup.agencyTier.cadence')}</div>
                <div className="inline-flex rounded-lg border border-border p-1 bg-card">
                  {(['monthly', 'annual'] as const).map(p => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setAgencyPeriod(p)}
                      className={cn(
                        'px-3 py-1.5 text-sm rounded-md transition-colors',
                        agencyPeriod === p ? 'bg-brand-primary text-white' : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {p === 'monthly' ? t('signup.agencyTier.monthly') : t('signup.agencyTier.annual')}
                    </button>
                  ))}
                </div>
              </div>

              {error && (
                <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 p-3">
                  <p className="text-sm text-destructive">{error}</p>
                  {agencyConflict && (
                    <a
                      href="mailto:support@hiromatch.com?subject=Agency%20account%20request"
                      className="inline-block mt-2 text-sm font-medium text-brand-accent hover:text-brand-primary"
                    >
                      {t('signup.agencyTier.contactSupport')} →
                    </a>
                  )}
                </div>
              )}

              <Button
                onClick={handleConfirmAgency}
                disabled={agencySubmitting || agencyConflict}
                className="w-full mt-6 bg-brand-primary hover:bg-brand-primary-mid text-white"
              >
                {agencySubmitting ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" />…</>
                ) : (
                  t('signup.agencyTier.continue')
                )}
              </Button>
            </div>
          )}
        </div>
      </div>

      <AgencyContactModal
        open={agencyContactOpen}
        onClose={() => setAgencyContactOpen(false)}
        context="signup"
      />
    </div>
  );
};

export default SignupPage;
